import React, { useState, useEffect } from "react";
import { Trash2, Eye, EyeOff } from "lucide-react";
import { useGayMode, useGayTags } from "../utils/gayMode";

const STORAGE_KEY = "tag-rules";

interface TagSettings {
  highlight: string[];
  block: string[];
}
const DEFAULT_BLOCK = ["无关内容"];
const OLD_DEFAULT_BLOCK = [
  "无关内容",
  "男性",
  "男娘",
  "人妖",
  "露屌",
  "阳痿",
  "男同",
];

const TagController = () => {
  const [gayMode, toggleGayMode] = useGayMode();
  const [gayTags, setGayTags, addGayTag, removeGayTag, resetGayTags] = useGayTags();
  const [inputValue, setInputValue] = useState("");
  const [highlight, setHighlight] = useState<string[]>(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (!savedData) return []; // 默认高亮为空

    try {
      const parsed: TagSettings = JSON.parse(savedData);
      return Array.isArray(parsed.highlight) ? parsed.highlight : [];
    } catch (e) {
      console.error("Failed to parse highlight settings", e);
      return [];
    }
  });
  const [block, setBlock] = useState<string[]>(() => {
    // 1. 尝试从 LocalStorage 读取
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (!savedData) return DEFAULT_BLOCK;

    try {
      const parsed: TagSettings = JSON.parse(savedData);
      let parsedBlock = parsed.block || [];
      const parsedHighlight = parsed.highlight || [];

      // 若保存的是旧版本默认的 7 个屏蔽词（包含 6 个 gay 标签），自动迁移为仅保留 ["无关内容"]
      const isOldDefault =
        parsedBlock.length === OLD_DEFAULT_BLOCK.length &&
        OLD_DEFAULT_BLOCK.every((item) => parsedBlock.includes(item));
      if (isOldDefault) {
        parsedBlock = ["无关内容"];
      }

      // 2. 检查 highlight 里面是否包含 DEFAULT_BLOCK 中的任何一个元素
      const hasIntersection = DEFAULT_BLOCK.some((item) =>
        parsedHighlight.includes(item),
      );

      if (!hasIntersection) {
        // 如果没有交集：返回 DEFAULT_BLOCK 和 parsedBlock 的并集
        return Array.from(new Set([...DEFAULT_BLOCK, ...parsedBlock]));
      }

      return parsedBlock.length === 0 ? ["无关内容"] : parsedBlock;
    } catch (e) {
      console.error("Failed to parse tag settings", e);
      return DEFAULT_BLOCK;
    }
  });
  // // 1. 初始化：从 LocalStorage 读取
  // useEffect(() => {
  //   const savedData = localStorage.getItem(STORAGE_KEY);
  //   if (savedData) {
  //     try {
  //       const parsed: TagSettings = JSON.parse(savedData);
  //       setHighlight(parsed.highlight || []);
  //       setBlock(
  //         ((parsed.block || [])?.length || 0) === 0
  //           ? ["无关内容"]
  //           : parsed.block || [],
  //       );
  //     } catch (e) {
  //       console.error("Failed to parse tag settings", e);
  //     }
  //   }
  // }, []);

  // 2. 监听变化：保存到 LocalStorage
  useEffect(() => {
    const data: TagSettings = { highlight, block };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [highlight, block]);

  // 添加 Tag 的逻辑 (type: 'highlight' | 'block')
  const addTag = (type: "highlight" | "block") => {
    const tag = inputValue.trim();
    if (!tag) return;

    if (type === "highlight") {
      // 如果存在于屏蔽列表，先移除
      setBlock((prev) => prev.filter((t) => t !== tag));
      // 添加到高亮列表（去重）
      setHighlight((prev) => Array.from(new Set([...prev, tag])));
    } else {
      // 如果存在于高亮列表，先移除
      setHighlight((prev) => prev.filter((t) => t !== tag));
      // 添加到屏蔽列表（去重）
      setBlock((prev) => Array.from(new Set([...prev, tag])));
    }
    setInputValue("");
  };

  const removeTag = (tag: string, type: "highlight" | "block") => {
    if (type === "highlight") {
      setHighlight((prev) => prev.filter((t) => t !== tag));
    } else {
      setBlock((prev) => prev.filter((t) => t !== tag));
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md border border-gray-200 max-w-md">
      <h3 className="text-lg font-bold mb-3 text-gray-800">标签显示控制</h3>

      {/* Gay 模式快捷开关 */}
      <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200 flex items-center justify-between">
        <div>
          <div className="font-semibold text-purple-900 text-sm flex items-center gap-1.5">
            <span>🌈</span> Gay 模式
          </div>
          <div className="text-xs text-purple-700 mt-0.5">
            {gayMode
              ? `已开启：只显示包含【${gayTags.join(" / ")}】的账号`
              : `已关闭：隐藏包含【${gayTags.join(" / ")}】的账号`}
          </div>
        </div>
        <button
          onClick={toggleGayMode}
          className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
            gayMode
              ? "bg-purple-600 text-white hover:bg-purple-700"
              : "bg-white text-purple-700 border border-purple-300 hover:bg-purple-100"
          }`}
        >
          {gayMode ? "已开启" : "开启"}
        </button>
      </div>

      {/* 输入区域 */}
      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTag("highlight")}
          placeholder="输入标签名称..."
          className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* 按钮区域 */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => addTag("highlight")}
          disabled={!inputValue.trim()}
          className="flex-1 flex items-center justify-center gap-1 bg-green-100 text-green-700 px-3 py-2 rounded hover:bg-green-200 disabled:opacity-50 transition"
        >
          <Eye size={16} /> 高亮
        </button>
        <button
          onClick={() => addTag("block")}
          disabled={!inputValue.trim()}
          className="flex-1 flex items-center justify-center gap-1 bg-red-100 text-red-700 px-3 py-2 rounded hover:bg-red-200 disabled:opacity-50 transition"
        >
          <EyeOff size={16} /> 屏蔽
        </button>
        <button
          onClick={() => {
            if (!inputValue.trim()) return;
            addGayTag(inputValue);
            setInputValue("");
          }}
          disabled={!inputValue.trim()}
          className="flex-1 flex items-center justify-center gap-1 bg-purple-100 text-purple-700 px-3 py-2 rounded hover:bg-purple-200 disabled:opacity-50 transition"
        >
          <span>🌈</span> 设为Gay
        </button>
      </div>

      <div className="space-y-4">
        {/* 高亮列表 */}
        <div>
          <h4 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            高亮显示 ({highlight.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {highlight.length === 0 && (
              <span className="text-xs text-gray-400">无高亮标签</span>
            )}
            {highlight.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-sm rounded border border-green-200"
              >
                {tag}
                <button
                  onClick={() => removeTag(tag, "highlight")}
                  className="hover:text-green-900"
                >
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>

        <hr className="border-gray-100" />

        {/* 屏蔽列表 */}
        <div>
          <h4 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            已屏蔽 ({block.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {block.length === 0 && (
              <span className="text-xs text-gray-400">无屏蔽标签</span>
            )}
            {block.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 text-sm rounded border border-red-200"
              >
                {tag}
                <button
                  onClick={() => removeTag(tag, "block")}
                  className="hover:text-red-900"
                >
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>

        <hr className="border-gray-100" />

        {/* Gay 标签列表 */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-500 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-purple-500"></span>
              Gay 专属标签 ({gayTags.length})
            </h4>
            <button
              onClick={resetGayTags}
              className="text-xs text-purple-600 hover:text-purple-800 underline"
            >
              恢复默认
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {gayTags.length === 0 && (
              <span className="text-xs text-gray-400">无 Gay 标签</span>
            )}
            {gayTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 text-sm rounded border border-purple-200"
              >
                {tag}
                <button
                  onClick={() => removeGayTag(tag)}
                  className="hover:text-purple-900"
                  title="从 Gay 列表中移除"
                >
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagController;
